import PropTypes from "prop-types";
import { useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components/macro";

import { useSignups } from "Signups/state";
import { AnimatePresence, motion } from "framer-motion";

import FnrDividerLine from "UI/FnrDividerLine/FnrDividerLine";
import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import { useSignupItem } from "../../EditSignupItemNav/statehooks";

import AddRespondent from "./AddRespondent";
import AddToGroup from "./AddToGroup";
import CancellationsList from "./CancellationsList";
import LinkToGroup from "./LinkToGroup";
import LinkedToGroupNotification from "./LinkedToGroupNotification";
import RespondentsList from "./RespondentsList";
import ShowCancellationsButton from "./ShowCancellationsButton";

Respondents.propTypes = {
  signupItemID: PropTypes.number,
};

export default function Respondents() {
  const { signupItemID: signupItemIDRaw } = useParams();
  const signupItemID = Number(signupItemIDRaw);
  const [
    {
      currentSignupList: {
        ID: signupListID,
        signupListTypeID,
        signupListStatusID,
        options: { expireDate },
        fundIt,
      },
    },
  ] = useSignups();
  const [itemInfo] = useSignupItem();

  const signupExpired =
    expireDate && expireDate > 0 && new Date(expireDate * 1000) < new Date();
  const signupArchived = signupListStatusID === 4;

  const slotsAvailable = itemInfo.totalSlots - itemInfo.slotsTaken;
  const [showCancellations, setShowCancellations] = useState(false);
  const hasSignupLevelCost = fundIt?.hasSignupCost;

  return (
    <Wrapper>
      <LinkedToGroupNotification
        {...itemInfo.linkedToGroup}
        signupItemID={signupItemID}
      />
      <Toolbar>
        <AddRespondent
          isFull={signupListTypeID !== 4 && slotsAvailable === 0}
          signupExpired={signupExpired}
          signupArchived={signupArchived}
          signupListID={signupListID}
          signupItemID={signupItemID}
        />

        {!(itemInfo.linkedToGroup || signupArchived) && (
          <LinkToGroup signupItemID={signupItemID} />
        )}
        {itemInfo.slotsTaken > 0 && <AddToGroup signupItemID={signupItemID} />}
      </Toolbar>

      {itemInfo.slotsTaken === 0 ? (
        <NoSignupsYet />
      ) : (
        <>
          <FnrDividerLine title="All Respondents" style={{ marginBottom: 0 }} />
          <RespondentsList
            signupItemID={signupItemID}
            slotsTaken={itemInfo.slotsTaken}
            slotsAvailable={slotsAvailable}
            maxSlots={itemInfo.maxSingleUserSlots}
            hasSignupLevelCost={hasSignupLevelCost}
            {...itemInfo}
          />
        </>
      )}

      <ShowCancellationsButton
        cancelledRespondents={itemInfo.cancelledRespondents}
        showCancellations={showCancellations}
        setShowCancellations={setShowCancellations}
      />

      <AnimatePresence>
        {showCancellations && itemInfo.cancelledRespondents?.length ? (
          <CancellationsWrapper
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FnrDividerLine title="Cancellations" style={{ marginBottom: 0 }} />
            <CancellationsList
              cancelledRespondents={itemInfo.cancelledRespondents}
              signupItemID={signupItemID}
              slotsAvailable={slotsAvailable}
            />
          </CancellationsWrapper>
        ) : null}
      </AnimatePresence>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding-top: 6px;
  padding-right: 18px;
  height: 100%;
  max-width: 460px;
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

function NoSignupsYet() {
  return (
    <NoSignups>
      <IconWrapper>
        <FnrIcon fal fa-walking />
      </IconWrapper>
      <NoSignupsTitle>
        No one has signed up for this item yet.
        <NoSignupsSubtext>
          {"As people sign up, they'll show up here."}
        </NoSignupsSubtext>
      </NoSignupsTitle>
    </NoSignups>
  );
}

const NoSignups = styled.div`
  color: ${colors.fnGray};
  font-weight: 400;
  display: flex;
  margin-top: 60px;
`;
const IconWrapper = styled.div`
  font-size: 48px;
  width: 50px;
  flex-shrink: 0;
  margin-right: 10px;
  text-align: center;
`;
const NoSignupsTitle = styled.div`
  font-size: 24px;
`;
const NoSignupsSubtext = styled.div`
  font-size: 16px;
`;

const CancellationsWrapper = styled(motion.div)`
  overflow: hidden;
  margin-left: 10px;
`;
